import * as React from 'react';
import {EventArg} from '@react-navigation/native';

import Form from './partials/Form';
import Modal, {ModalParams} from './partials/Modal';
import UnregisteredConfirmModal from './partials/UnregisteredConfirmModal';
import RestoreModal from './partials/RestoreModal';

import Layout from '../shared/Layout';
import AlertModal from '../shared/modals/AlertModal';
import shouldUpdateEntityList from '../shared/enhanced/shouldUpdateEntityList';

import {equalForKeys} from '../../helpers/equalForKeys';

import NavigationProp from '../../navigators/NavigationProp';
import {NewSceneRouteProp} from '../../navigators/RouteProps';

import * as routers from '../../routers';

import {formatErrorMessages} from '../../helpers/errorMessages';

import CharacterPatternMapping from '../../view_models/CharacterPatternMapping';
import CoachmarkState from '../../view_models/CoachmarkState';
import {visibleRestoreModal} from '../../view_models/controlFlags';

import {Params as ActorCharacterFaceIndexParams} from '../../actions/actor_character_faces/index';
import {Params as MarkIndexParams} from '../../actions/marks/index';
import {Params as SpeechBalloonIndexParams} from '../../actions/speech_balloons/index';
import {Params as OrientedSpeechBalloonIndexParams} from '../../actions/oriented_speech_balloons/index';
import {Params as SceneCommandFormListsCreateParams} from '../../actions/scene_command_form_lists/create';
import {Params as SceneCommandFormListsUpdateParams} from '../../actions/scene_command_form_lists/update';
import {Params as SceneCommandFormCreateParams} from '../../actions/scene_command_forms/create';
import {Params as SceneCommandFormDestroyParams} from '../../actions/scene_command_forms/destroy';
import {Params as SceneCommandFormUpdateParams} from '../../actions/scene_command_forms/update';
import {Params as SceneFormCreateParams} from '../../actions/scene_forms/create';
import {Params as SceneFormUpdateParams} from '../../actions/scene_forms/update';
import {Params as SceneScriptCreateParams} from '../../actions/scene_scripts/create';
import {Params as SceneIndexParams} from '../../actions/scenes/index';
import {Params as SceneCreateParams} from '../../actions/scenes/create';
import {Params as FullScreenIllustrationIndexParams} from '../../actions/full_screen_illustrations/index';
import {Params as TextFrameIndexParams} from '../../actions/text_frames/index';
import {Params as CharacterPatternIndexParams} from '../../actions/character_patterns/index';
import {Params as CharacterPatternCreateParams} from '../../actions/character_patterns/create';
import {Params as ActorCostumeIndexParams} from '../../actions/actor_costumes/index';
import {Params as ActorHairStyleIndexParams} from '../../actions/actor_hair_styles/index';
import {Params as ActorAccessorySetIndexParams} from '../../actions/actor_accessory_sets/index';
import {Params as VoiceCreateParams} from '../../actions/voices/create';
import {Params as VoiceUpdateParams} from '../../actions/voices/update';
import {Params as CoachmarkModalUpdateParams} from '../../actions/coachmark_modal/update';
import {Params as ApplicationActionModalUpdateParams} from '../../actions/application_action_modal/update';

import {QueryState} from '../../reducers/queries/Response';

import {CharacterSceneCommandForm} from '../../view_models/SceneFrame';

import CurrentUser from '../../../domain/entities/writer/CurrentUser';
import CurrentUserStatus from '../../../domain/entities/writer/CurrentUserStatus';
import ActorCharacterFace from '../../../domain/entities/ActorCharacterFace';
import Mark from '../../../domain/entities/Mark';
import SpeechBalloon from '../../../domain/entities/SpeechBalloon';
import OrientedSpeechBalloon from '../../../domain/entities/OrientedSpeechBalloon';
import TextFrame from '../../../domain/entities/TextFrame';
import FullScreenIllustration from '../../../domain/entities/FullScreenIllustration';
import Episode from '../../../domain/entities/Episode';
import Scene from '../../../domain/entities/Scene';
import CharacterPattern from '../../../domain/entities/CharacterPattern';
import ActorCostume from '../../../domain/entities/ActorCostume';
import ActorHairStyle from '../../../domain/entities/ActorHairStyle';
import ActorAccessorySet from '../../../domain/entities/ActorAccessorySet';
import Voice from '../../../domain/entities/Voice';
import Sound from '../../../domain/entities/Sound';
import PaginatedResult from '../../../domain/results/PaginatedResult';
import SceneCommandForm from '../../../domain/forms/scene_commands/SceneCommandForm';
import SceneForm from '../../../domain/forms/SceneForm';
import SceneCommandFormsPositionSwitcher from '../../../domain/services/scene_commands/SceneCommandFormsPositionSwitcher';
import SceneCommandParamsBuilder from '../../../domain/services/scene_commands/SceneCommandParamsBuilder';
import CharacterHideSceneCommandForm from '../../../domain/forms/scene_commands/CharacterHideSceneCommandForm';
import DescriptiveTextShowSceneCommandForm from '../../../domain/forms/scene_commands/DescriptiveTextShowSceneCommandForm';
import FullScreenIllustrationShowSceneCommandForm from '../../../domain/forms/scene_commands/FullScreenIllustrationShowSceneCommandForm';
import IllustrationShowSceneCommandForm from '../../../domain/forms/scene_commands/IllustrationShowSceneCommandForm';
import EffectShowSceneCommandForm from '../../../domain/forms/scene_commands/EffectShowSceneCommandForm';
import SoundEffectShowSceneCommandForm from '../../../domain/forms/scene_commands/SoundEffectShowSceneCommandForm';
import BackgroundMusicShowSceneCommandForm from '../../../domain/forms/scene_commands/BackgroundMusicShowSceneCommandForm';
import BackgroundMusicHideSceneCommandForm from '../../../domain/forms/scene_commands/BackgroundMusicHideSceneCommandForm';
import SpeechTextShowSceneCommandForm from '../../../domain/forms/scene_commands/SpeechTextShowSceneCommandForm';
import CompositeSequenceSceneCommandForm from '../../../domain/forms/scene_commands/CompositeSequenceSceneCommandForm';
import Position from '../../../domain/value_objects/Position';

import StorageAutoSaveCreatingScenesRepository from '../../../data/repositories/StorageAutoSaveCreatingScenesRepository';

const autoSaveCreatingScenesRepository =
  new StorageAutoSaveCreatingScenesRepository();

export interface Params {
  episodeId: number;
  firstScene?: boolean;
  episodePurgeForBack?: boolean;
  fromEpisode?: boolean;
}

export interface StateProps {
  navigation: NavigationProp;
  route: NewSceneRouteProp;
  currentUser: CurrentUser | null;
  currentUserStatus: CurrentUserStatus | null;
  episode: Episode | null;
  actorCharacterFaceEntities: {[key: number]: ActorCharacterFace};
  actorCharacterFaceQueries: QueryState;
  marksParams: MarkIndexParams;
  marks: Mark[] | null;
  sceneForm: SceneForm | null;
  sceneCommandForms: SceneCommandForm[] | null;
  speechBalloons: SpeechBalloon[] | null;
  speechBalloonsParams: SpeechBalloonIndexParams;
  orientedSpeechBalloons: OrientedSpeechBalloon[] | null;
  orientedSpeechBalloonsParams: OrientedSpeechBalloonIndexParams;
  textFrames: TextFrame[] | null;
  textFramesParams: TextFrameIndexParams;
  fullScreenIllustrations: FullScreenIllustration[] | null;
  fullScreenIllustrationsParams: FullScreenIllustrationIndexParams;
}

export interface DispatchProps {
  showCurrentUserStatus: () => Promise<CurrentUserStatus>;
  indexActorCharacterFaces: (
    params: ActorCharacterFaceIndexParams,
  ) => Promise<PaginatedResult<ActorCharacterFace>>;
  indexMarks: (params: MarkIndexParams) => Promise<PaginatedResult<Mark>>;
  showEpisode: (episodeId: number, forceUpdate?: boolean) => Promise<Episode>;
  indexScenes: (params: SceneIndexParams) => Promise<PaginatedResult<Scene>>;
  createScene: (params: SceneCreateParams) => Promise<Scene>;
  createSceneForm: (params: SceneFormCreateParams) => Promise<SceneForm>;
  updateSceneForm: (params: SceneFormUpdateParams) => Promise<SceneForm>;
  destroySceneForm: () => Promise<null>;
  createSceneCommandFormLists: (
    params: SceneCommandFormListsCreateParams,
  ) => Promise<SceneCommandForm[]>;
  updateSceneCommandFormLists: (
    params: SceneCommandFormListsUpdateParams,
  ) => Promise<SceneCommandForm[]>;
  createSceneCommandForm: (
    params: SceneCommandFormCreateParams,
  ) => Promise<any>;
  destroySceneCommandForm: (
    params: SceneCommandFormDestroyParams,
  ) => Promise<any>;
  updateSceneCommandForm: (
    params: SceneCommandFormUpdateParams,
  ) => Promise<any>;
  createSceneScript: (params: SceneScriptCreateParams) => Promise<any>;
  destroyEpisode: (id: number) => Promise<number>;
  destroyStory: (id: number) => Promise<number>;
  indexFullScreenIllustrations: (
    params: FullScreenIllustrationIndexParams,
  ) => Promise<PaginatedResult<FullScreenIllustration>>;
  indexSpeechBalloons: (
    params: SpeechBalloonIndexParams,
  ) => Promise<PaginatedResult<SpeechBalloon>>;
  indexOrientedSpeechBalloons: (
    params: OrientedSpeechBalloonIndexParams,
  ) => Promise<PaginatedResult<OrientedSpeechBalloon>>;
  indexTextFrames: (
    params: TextFrameIndexParams,
  ) => Promise<PaginatedResult<TextFrame>>;
  indexCharacterPatterns: (
    params: CharacterPatternIndexParams,
  ) => Promise<PaginatedResult<CharacterPattern>>;
  createCharacterPattern: (
    params: CharacterPatternCreateParams,
  ) => Promise<CharacterPattern>;
  updateCharacterPatternUsageHistory: (id: number) => Promise<CharacterPattern>;
  indexActorCostumes: (
    params: ActorCostumeIndexParams,
  ) => Promise<PaginatedResult<ActorCostume>>;
  indexActorHairStyles: (
    params: ActorHairStyleIndexParams,
  ) => Promise<PaginatedResult<ActorHairStyle>>;
  indexActorAccessorySets: (
    params: ActorAccessorySetIndexParams,
  ) => Promise<PaginatedResult<ActorAccessorySet>>;
  createVoice: (params: VoiceCreateParams) => Promise<Voice>;
  updateVoice: (id: number, params: VoiceUpdateParams) => Promise<Voice>;
  updateCoachmarkModal: (params: CoachmarkModalUpdateParams) => void;
  updateApplicationActionModal: (
    params: ApplicationActionModalUpdateParams,
  ) => void;
}

interface Props extends StateProps, DispatchProps {}

interface State {
  loading: boolean;
  disabledSubmitButton: boolean;
  episode: Episode | null;
  sceneForm: SceneForm | null;
  sceneCommandForms: SceneCommandForm[] | null;
  modalParams: ModalParams | null;
  alertMessage?: string;
  visibleRestoredModal?: boolean;
  beforeBackCallback: (() => void) | null;
}

export default class New extends React.Component<Props, State> {
  private formRef = React.createRef<Form>();

  private mounted = false;

  private editing = true;

  constructor(props: Props) {
    super(props);
    this.state = {
      loading: false,
      disabledSubmitButton: true,
      episode: null,
      sceneCommandForms: null,
      sceneForm: null,
      modalParams: null,
      beforeBackCallback: null,
    };
  }

  public static getDerivedStateFromProps(
    nextProps: Readonly<Props>,
    prevState: State,
  ): Partial<State> | null {
    if (
      !prevState.disabledSubmitButton &&
      !nextProps.sceneForm?.background &&
      (nextProps.sceneCommandForms?.length || 0) === 0
    ) {
      return {disabledSubmitButton: true};
    } else if (
      prevState.disabledSubmitButton &&
      (nextProps.sceneForm?.background ||
        (nextProps.sceneCommandForms?.length || 0) > 0)
    ) {
      return {disabledSubmitButton: false};
    }
    return null;
  }

  public shouldComponentUpdate(
    nextProps: Readonly<Props>,
    nextState: Readonly<State>,
  ): boolean {
    if (
      !equalForKeys(this.props, nextProps, [
        'sceneForm',
        'sceneCommandForms',
        'currentUserStatus',
      ])
    ) {
      return true;
    }
    if (!equalForKeys(this.state, nextState)) {
      return true;
    }
    if (nextState.modalParams) {
      if (
        !equalForKeys(this.props, nextProps, [
          'actorCharacterFaceEntities',
          'actorCharacterFaceQueries',
          'marksParams',
          'speechBalloonsParams',
          'orientedSpeechBalloonsParams',
          'textFramesParams',
        ])
      ) {
        return true;
      }
      if (
        shouldUpdateEntityList(
          {
            entities: this.props.marks,
          },
          {
            entities: nextProps.marks,
          },
        )
      ) {
        return true;
      }
      if (
        shouldUpdateEntityList(
          {
            entities: this.props.speechBalloons,
          },
          {
            entities: nextProps.speechBalloons,
          },
        )
      ) {
        return true;
      }
      if (
        shouldUpdateEntityList(
          {
            entities: this.props.orientedSpeechBalloons,
          },
          {
            entities: nextProps.orientedSpeechBalloons,
          },
        )
      ) {
        return true;
      }
      if (
        shouldUpdateEntityList(
          {
            entities: this.props.textFrames,
          },
          {
            entities: nextProps.textFrames,
          },
        )
      ) {
        return true;
      }
    }
    return false;
  }

  public componentDidMount() {
    this.mounted = true;
    const {route, showCurrentUserStatus} = this.props;
    this.addListenerBeforeRemove();
    showCurrentUserStatus();
    this.buildSceneFormFromClient();
  }

  public componentDidUpdate(
    prevProps: Props,
    prevState: State,
    prevContext: any,
  ) {
    const {route, sceneForm, sceneCommandForms} = this.props;
    const {episodeId} = route.params;
    if (
      sceneForm &&
      sceneCommandForms &&
      this.state.sceneForm &&
      this.state.sceneCommandForms
    ) {
      if (this.changedForm()) {
        autoSaveCreatingScenesRepository.update(episodeId, {
          sceneForm,
          sceneCommandForms,
          updatedAt: new Date(),
        });
      } else {
        autoSaveCreatingScenesRepository.destroy(episodeId);
      }
    }
    if (!this.props.sceneCommandForms || !prevProps.sceneCommandForms) {
      return;
    }
    const currentSceneCommandIds = this.props.sceneCommandForms.map(s => {
      return s.sceneCommandId;
    });
    const prevSceneCommandIds = prevProps.sceneCommandForms.map(s => {
      return s.sceneCommandId;
    });
    const ret = prevSceneCommandIds.every((prevSceneCommandId, index) => {
      return prevSceneCommandId === currentSceneCommandIds[index];
    });
    if (!ret) {
      return;
    }
    const currentLastSceneCommandForm =
      this.props.sceneCommandForms[this.props.sceneCommandForms.length - 1];
    const prevLastSceneCommandForm =
      prevProps.sceneCommandForms[prevProps.sceneCommandForms.length - 1];
    if (
      currentLastSceneCommandForm instanceof
        CompositeSequenceSceneCommandForm &&
      (!(
        prevLastSceneCommandForm instanceof CompositeSequenceSceneCommandForm
      ) ||
        (prevLastSceneCommandForm instanceof
          CompositeSequenceSceneCommandForm &&
          prevLastSceneCommandForm.commandForms.length <
            currentLastSceneCommandForm.commandForms.length))
    ) {
      //
    } else if (currentSceneCommandIds.length <= prevSceneCommandIds.length) {
      return;
    }
    if (this.formRef.current) {
      this.formRef.current.scrollToEnd();
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
    const {navigation, route, updateApplicationActionModal} = this.props;
    if (this.editing && this.changedForm()) {
      updateApplicationActionModal({
        action: {
          message:
            '作成中に画面を遷移したためシーンが\n保存されていません。\n作成画面へ戻りますか？',
          callback: () => {
            visibleRestoreModal.value = false;
            navigation.navigate('NewScene', route.params);
          },
        },
      });
    }
  }

  public render(): React.ReactNode {
    const {
      route,
      navigation,
      currentUser,
      currentUserStatus,
      sceneForm,
      sceneCommandForms,
      actorCharacterFaceEntities,
      actorCharacterFaceQueries,
      marks,
      speechBalloons,
      orientedSpeechBalloons,
      textFrames,
    } = this.props;
    const {
      loading,
      episode,
      modalParams,
      disabledSubmitButton,
      alertMessage,
      visibleRestoredModal,
    } = this.state;
    const {firstScene} = route.params;
    return (
      <Layout
        title={`シーン${
          (episode && episode.scenesCount ? episode.scenesCount : 0) + 1
        }の作成`}
        navigation={navigation}
        scrollable={false}
        back={!firstScene}
        loading={loading}
        alwaysEnableBackButton={true}>
        {this.state.sceneForm &&
          this.state.sceneCommandForms &&
          sceneForm &&
          sceneCommandForms && (
            <Form
              ref={this.formRef}
              sceneForm={sceneForm}
              sceneCommandForms={sceneCommandForms}
              newScene={true}
              enableFullScreenIllustration={currentUserStatus?.extensionsCodes.includes(
                'full_screen_illustration',
              )}
              enableSoundEffect={currentUserStatus?.extensionsCodes.includes(
                'sound_effect',
              )}
              enableBackgroundMusic={currentUserStatus?.extensionsCodes.includes(
                'background_music',
              )}
              disabledSubmitButton={disabledSubmitButton}
              onForwardToBackgrounds={this.handleForwardToBackgrounds}
              onForwardToBackgroundFilters={
                this.handleForwardToBackgroundFilters
              }
              onForwardToBackgroundEffects={
                this.handleForwardToBackgroundEffects
              }
              onForwardToCharacters={this.handleForwardToCharacters}
              onForwardToSpeechBalloons={this.handleForwardToSpeechBalloons}
              onForwardToTextFrames={this.handleForwardToTextFrames}
              onForwardToIllustrations={this.handleForwardToIllustrations}
              onForwardToFullScreenIllustrations={
                this.handleForwardToFullScreenIllustrations
              }
              onForwardToEffects={this.handleForwardToEffects}
              onForwardToSoundEffects={this.handleForwardToSoundEffects}
              onForwardToBackgroundMusic={this.handleForwardToBackgroundMusic}
              onChangeOrder={this.handleChangeOrder}
              onRequestOpenModal={this.handleRequestOpenModal}
              onRequestUpdateCoachmarkModal={
                this.handleRequestUpdateCoachmarkModal
              }
              onPreview={this.handlePreview}
              onSubmit={this.handleSubmit}
            />
          )}
        <UnregisteredConfirmModal
          visible={this.state.beforeBackCallback != null}
          onDelete={this.handleDelete}
          onCancel={this.handleCancel}
        />
        {modalParams && sceneForm?.storyId && (
          <Modal
            storyId={sceneForm.storyId}
            modalParams={modalParams}
            actorCharacterFaceEntities={actorCharacterFaceEntities}
            actorCharacterFaceQueries={actorCharacterFaceQueries}
            marks={marks}
            speechBalloons={speechBalloons}
            orientedSpeechBalloons={orientedSpeechBalloons}
            textFrames={textFrames}
            enableVoice={currentUserStatus?.extensionsCodes.includes('voice')}
            enableSoundEffect={currentUserStatus?.extensionsCodes.includes(
              'sound_effect',
            )}
            enableTrimming={currentUser?.enabledPaidSubscriber}
            onRequestCloseModal={this.handleRequestCloseModal}
            onForwardToCharacters={this.handleForwardToCharacters}
            onChangeCharacterShowOrUpdateCommand={
              this.handleChangeCharacterShowOrUpdateCommand
            }
            onInsertCharacterUpdateCommandAt={
              this.handleInsertCharacterUpdateCommandAt
            }
            onRemoveCharacterShowOrUpdateCommand={
              this.handleRemoveCharacterShowOrUpdateCommand
            }
            onInsertCharacterHideCommandAt={
              this.handleInsertCharacterHideCommandAt
            }
            onRemoveCharacterHideCommand={this.handleRemoveCharacterHideCommand}
            onChangeSpeechTextShowSceneCommandForm={
              this.handleChangeSpeechTextShowSceneCommandForm
            }
            onRemoveSpeechTextShowCommand={
              this.handleRemoveSpeechTextShowCommand
            }
            onChangeDescriptiveTextShowSceneCommandForm={
              this.handleChangeDescriptiveTextShowSceneCommandForm
            }
            onRemoveDescriptiveTextShowCommand={
              this.handleRemoveDescriptiveTextShowCommand
            }
            onForwardToChangeIllustrations={
              this.handleForwardToChangeIllustrations
            }
            onRemoveIllustrationShowCommand={
              this.handleRemoveIllustrationShowCommand
            }
            onChangeIllustrationShowSceneCommandForm={
              this.handleChangeIllustrationShowSceneCommandForm
            }
            onForwardToAddSoundEffects={this.handleForwardToAddSoundEffects}
            onForwardToChangeFullScreenIllustrations={
              this.handleForwardToChangeFullScreenIllustrations
            }
            onRemoveFullScreenIllustrationShowCommand={
              this.handleRemoveFullScreenIllustrationShowCommand
            }
            onChangeFullScreenIllustrationShowSceneCommandForm={
              this.handleChangeFullScreenIllustrationShowSceneCommandForm
            }
            onForwardToChangeEffects={this.handleForwardToChangeEffects}
            onRemoveEffectShowCommand={this.handleRemoveEffectShowCommand}
            onChangeEffectShowSceneCommandForm={
              this.handleChangeEffectShowSceneCommandForm
            }
            onForwardToChangeSoundEffects={
              this.handleForwardToChangeSoundEffects
            }
            onRemoveSoundEffectShowCommand={
              this.handleRemoveSoundEffectShowCommand
            }
            onForwardToBackgroundMusic={this.handleForwardToBackgroundMusic}
            onForwardToChangeBackgroundMusic={
              this.handleForwardToChangeBackgroundMusic
            }
            onRemoveBackgroundMusicShowCommand={
              this.handleRemoveBackgroundMusicShowCommand
            }
            onInsertBackgroundMusicHideCommandAt={
              this.handleInsertBackgroundMusicHideCommandAt
            }
            onChangeCommand={this.handleChangeCommand}
            onRemoveCommand={this.handleRemoveCommand}
            onRequestIndexActorCharacterFaces={
              this.handleRequestIndexActorCharacterFaces
            }
            onRequestIndexMarks={this.handleRequestIndexMarks}
            onRequestIndexSpeechBalloons={this.handleRequestIndexSpeechBalloons}
            onRequestIndexOrientedSpeechBalloons={
              this.handleRequestIndexOrientedSpeechBalloons
            }
            onRequestIndexTextFrames={this.handleRequestIndexTextFrames}
            onRequestCreateCharacterPattern={
              this.handleRequestCreateCharacterPattern
            }
            onRequestIndexActorCostumes={this.handleRequestIndexActorCostumes}
            onRequestIndexActorHairStyles={
              this.handleRequestIndexActorHairStyles
            }
            onRequestIndexActorAccessorySets={
              this.handleRequestIndexActorAccessorySets
            }
            onRequestUpdateModal={this.handleRequestUpdateModal}
          />
        )}
        <AlertModal
          visible={!!alertMessage}
          onCloseModal={this.handleCloseModal}>
          {alertMessage}
        </AlertModal>
        <RestoreModal
          visible={!!visibleRestoredModal}
          mode={'creating'}
          onRequestClose={this.handleCloseRestoredModal}
        />
      </Layout>
    );
  }

  private buildSceneFormFromServer = () => {
    const {
      navigation,
      route,
      episode,
      showEpisode,
      createSceneForm,
      createSceneCommandFormLists,
    } = this.props;
    const {episodeId} = route.params;
    if (!this.mounted) {
      return;
    }
    const createSceneFormByEpisode = (episode: Episode) => {
      if (!this.mounted) {
        return;
      }
      createSceneForm({
        episodeId: episode.id,
        storyId: episode.storyId,
      }).then(sceneForm => {
        this.setStateIfMounted({sceneForm, episode});
      });
    };
    if (episode) {
      createSceneFormByEpisode(episode);
    } else {
      showEpisode(episodeId)
        .then(createSceneFormByEpisode)
        .catch(e => {
          if (e.status === 401 || e.status === 404) {
            navigation.goBack();
          }
        });
    }
    createSceneCommandFormLists({sceneCommandForms: []}).then(
      sceneCommandForms => {
        this.setStateIfMounted({sceneCommandForms});
      },
    );
  };

  private buildSceneFormFromClient = async () => {
    const {route, updateSceneForm, showEpisode, createSceneCommandFormLists} =
      this.props;
    const {episodeId} = route.params;
    try {
      const autoSaveCreatingScene = await autoSaveCreatingScenesRepository.find(
        episodeId,
      );
      const episode = await showEpisode(episodeId);
      if (!this.mounted) {
        return;
      }
      updateSceneForm(autoSaveCreatingScene.sceneForm).then(sceneForm => {
        this.setStateIfMounted({sceneForm, episode});
      });
      createSceneCommandFormLists({
        sceneCommandForms: autoSaveCreatingScene.sceneCommandForms,
      }).then(sceneCommandForms => {
        this.setStateIfMounted({
          sceneCommandForms,
          visibleRestoredModal: visibleRestoreModal.value && true,
        });
        visibleRestoreModal.value = true;
      });
    } catch (e) {
      this.buildSceneFormFromServer();
    }
  };

  private addListenerBeforeRemove = () => {
    const {navigation} = this.props;
    navigation.addListener('beforeRemove', this.beforeRemoveCallback);
  };

  private removeListenerBeforeRemove = () => {
    const {navigation} = this.props;
    navigation.removeListener('beforeRemove', this.beforeRemoveCallback);
  };

  private beforeRemoveCallback = (
    e: EventArg<
      'beforeRemove',
      true,
      {
        action: Readonly<{
          type: string;
          payload?: any;
          source?: string | undefined;
          target?: string | undefined;
        }>;
      }
    >,
  ) => {
    if (e.data.action.type === 'RESET') {
      e.preventDefault();
      return;
    }
    const {navigation, sceneForm, sceneCommandForms} = this.props;
    if (!navigation.isFocused()) {
      return;
    }
    const callback = () => navigation.dispatch(e.data.action);
    if (!sceneForm || !sceneCommandForms) {
      return;
    }
    if (this.changedForm()) {
      e.preventDefault();
      this.setState({beforeBackCallback: callback});
    } else {
      this.purgeEpisode(sceneForm.episodeId);
    }
  };

  private changedForm = () => {
    const {sceneForm, sceneCommandForms} = this.props;
    if (!sceneForm || !sceneCommandForms) {
      return false;
    }
    return sceneForm.background || sceneCommandForms.length > 0;
  };

  private handleForwardToBackgrounds = () => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormBackgroundShowSceneCommandNewNavigations(
      navigation,
      {storyId, episodeId},
    );
  };

  private handleForwardToBackgroundFilters = () => {
    const {navigation, route} = this.props;
    const {episodeId} = route.params;
    routers.linkToSceneFormBackgroundShowSceneCommandNewBackgroundFiltersNavigations(
      navigation,
      {episodeId},
    );
  };

  private handleForwardToBackgroundEffects = () => {
    const {navigation, route} = this.props;
    const {episodeId} = route.params;
    routers.linkToSceneFormBackgroundShowSceneCommandNewBackgroundEffectsNavigations(
      navigation,
      {episodeId},
    );
  };

  private handleForwardToCharacters = (params: {
    sceneCommandIndex?: number;
    subSceneCommandIndex?: number;
    parentSceneCommandId?: number;
    position?: Position;
  }) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const storyId = sceneForm.storyId;
    // tslint:disable-next-line:max-line-length
    routers.linkToSceneFormCharacterShowSceneCommandNewNavigations(navigation, {
      storyId,
      episodeId,
      ...params,
    });
  };

  private handleChangeCharacterShowOrUpdateCommand = (
    sceneCommandForm: CharacterSceneCommandForm,
    characterPatternMapping?: CharacterPatternMapping,
  ) => {
    const {updateSceneCommandForm} = this.props;
    updateSceneCommandForm({sceneCommandForm, characterPatternMapping});
  };

  private handleInsertCharacterUpdateCommandAt = (
    sceneCommandForm: CharacterSceneCommandForm,
    sceneCommandIndex: number,
    characterPatternMapping?: CharacterPatternMapping,
  ) => {
    const {createSceneCommandForm} = this.props;
    createSceneCommandForm({
      index: sceneCommandIndex,
      sceneCommandForm,
      characterPatternMapping,
    });
  };

  private handleRemoveCharacterShowOrUpdateCommand = (
    sceneCommandForm: CharacterSceneCommandForm,
  ) => {
    const {destroySceneCommandForm} = this.props;
    destroySceneCommandForm({sceneCommandForm});
  };

  private handleInsertCharacterHideCommandAt = (
    sceneCommandForm: CharacterSceneCommandForm,
    sceneCommandIndex: number,
  ) => {
    const {createSceneCommandForm} = this.props;
    createSceneCommandForm({
      index: sceneCommandIndex,
      sceneCommandForm: new CharacterHideSceneCommandForm(
        sceneCommandForm.characterPattern,
        sceneCommandForm.actorCharacterFace,
        sceneCommandForm.mark,
        sceneCommandForm.position,
      ),
    });
  };

  private handleRemoveCharacterHideCommand = (
    sceneCommandForm: CharacterHideSceneCommandForm,
  ) => {
    const {destroySceneCommandForm} = this.props;
    destroySceneCommandForm({sceneCommandForm});
  };

  private handleForwardToSpeechBalloons = (params: {
    sceneCommandIndex?: number;
    subSceneCommandIndex?: number;
    parentSceneCommandId?: number;
    positions?: Position[];
  }) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    const storyId = sceneForm?.storyId;
    if (!storyId) {
      return;
    }
    const {positions} = params;
    routers.linkToSceneFormSpeechTextShowSceneCommandNewNavigations(
      navigation,
      {
        storyId,
        episodeId,
        lazyload: !!positions,
        ...params,
      },
    );
    if (positions) {
      setTimeout(() => {
        routers.linkToSceneFormSpeechTextShowSceneCommandNewSpeechTexts(
          navigation,
          {
            storyId,
            episodeId,
            ...params,
            positions,
            speechBalloonId: 1,
          },
        );
      }, 10);
    }
  };

  private handleChangeSpeechTextShowSceneCommandForm = (
    sceneCommandForm: SpeechTextShowSceneCommandForm,
  ) => {
    const {updateSceneCommandForm} = this.props;
    updateSceneCommandForm({sceneCommandForm});
  };

  private handleRemoveSpeechTextShowCommand = (
    sceneCommandForm: SpeechTextShowSceneCommandForm,
  ) => {
    const {destroySceneCommandForm} = this.props;
    destroySceneCommandForm({sceneCommandForm});
  };

  private handleForwardToTextFrames = (params: {
    sceneCommandIndex?: number;
    subSceneCommandIndex?: number;
    parentSceneCommandId?: number;
  }) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    const storyId = sceneForm?.storyId;
    if (!storyId) {
      return;
    }
    routers.linkToSceneFormDescriptiveTextShowSceneCommandNewNavigations(
      navigation,
      {
        storyId,
        episodeId,
        ...params,
      },
    );
  };

  private handleChangeDescriptiveTextShowSceneCommandForm = (
    sceneCommandForm: DescriptiveTextShowSceneCommandForm,
  ) => {
    const {updateSceneCommandForm} = this.props;
    updateSceneCommandForm({sceneCommandForm});
  };

  private handleRemoveDescriptiveTextShowCommand = (
    sceneCommandForm: DescriptiveTextShowSceneCommandForm,
  ) => {
    const {destroySceneCommandForm} = this.props;
    destroySceneCommandForm({sceneCommandForm});
  };

  private handleForwardToIllustrations = (params: {
    sceneCommandIndex?: number;
    subSceneCommandIndex?: number;
    parentSceneCommandId?: number;
  }) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormIllustrationShowSceneCommandNewNavigations(
      navigation,
      {
        storyId,
        episodeId,
        ...params,
      },
    );
  };

  private handleForwardToChangeIllustrations = (
    sceneCommandForm: IllustrationShowSceneCommandForm,
    parentSceneCommandId?: number,
  ) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const sceneCommandId = sceneCommandForm.sceneCommandId;
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormIllustrationShowSceneCommandEditNavigations(
      navigation,
      {
        storyId,
        episodeId,
        sceneCommandId,
        parentSceneCommandId,
      },
    );
  };

  private handleRemoveIllustrationShowCommand = (
    sceneCommandForm: IllustrationShowSceneCommandForm,
  ) => {
    const {destroySceneCommandForm} = this.props;
    destroySceneCommandForm({sceneCommandForm});
  };

  private handleChangeIllustrationShowSceneCommandForm = (
    sceneCommandForm: IllustrationShowSceneCommandForm,
  ) => {
    const {updateSceneCommandForm} = this.props;
    updateSceneCommandForm({sceneCommandForm});
  };

  private handleForwardToAddSoundEffects = (
    sceneCommandForm:
      | FullScreenIllustrationShowSceneCommandForm
      | IllustrationShowSceneCommandForm
      | EffectShowSceneCommandForm
      | SpeechTextShowSceneCommandForm
      | DescriptiveTextShowSceneCommandForm,
    parentSceneCommandId?: number,
    callback?: (sound: Sound) => void,
  ) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormSoundEffectShowSceneCommandEditNavigations(
      navigation,
      {
        storyId,
        episodeId,
        sceneCommandId: sceneCommandForm.sceneCommandId,
        parentSceneCommandId,
        callback,
      },
    );
  };

  private handleForwardToFullScreenIllustrations = (params: {
    sceneCommandIndex?: number;
    subSceneCommandIndex?: number;
    parentSceneCommandId?: number;
  }) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormFullScreenIllustrationShowSceneCommandNewNavigations(
      navigation,
      {
        storyId,
        episodeId,
        ...params,
      },
    );
  };

  private handleForwardToEffects = (params: {
    sceneCommandIndex?: number;
    subSceneCommandIndex?: number;
    parentSceneCommandId?: number;
  }) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormEffectShowSceneCommandNewNavigations(navigation, {
      storyId,
      episodeId,
      ...params,
    });
  };

  private handleForwardToSoundEffects = (params: {
    sceneCommandIndex?: number;
    subSceneCommandIndex?: number;
    parentSceneCommandId?: number;
  }) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormSoundEffectShowSceneCommandNewNavigations(
      navigation,
      {
        storyId,
        episodeId,
        ...params,
      },
    );
  };

  private handleForwardToBackgroundMusic = (params: {
    sceneCommandIndex?: number;
    subSceneCommandIndex?: number;
    parentSceneCommandId?: number;
  }) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormBackgroundMusicShowSceneCommandNewNavigations(
      navigation,
      {
        storyId,
        episodeId,
        ...params,
      },
    );
  };

  private handleForwardToChangeFullScreenIllustrations = (
    sceneCommandForm: FullScreenIllustrationShowSceneCommandForm,
    parentSceneCommandId?: number,
  ) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const sceneCommandId = sceneCommandForm.sceneCommandId;
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormFullScreenIllustrationShowSceneCommandEditNavigations(
      navigation,
      {
        storyId,
        episodeId,
        sceneCommandId,
        parentSceneCommandId,
      },
    );
  };

  private handleRemoveFullScreenIllustrationShowCommand = (
    sceneCommandForm: FullScreenIllustrationShowSceneCommandForm,
  ) => {
    const {destroySceneCommandForm} = this.props;
    destroySceneCommandForm({sceneCommandForm});
  };

  private handleChangeFullScreenIllustrationShowSceneCommandForm = (
    sceneCommandForm: FullScreenIllustrationShowSceneCommandForm,
  ) => {
    const {updateSceneCommandForm} = this.props;
    updateSceneCommandForm({sceneCommandForm});
  };

  private handleForwardToChangeEffects = (
    sceneCommandForm: EffectShowSceneCommandForm,
    parentSceneCommandId?: number,
  ) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const sceneCommandId = sceneCommandForm.sceneCommandId;
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormEffectShowSceneCommandEditNavigations(navigation, {
      storyId,
      episodeId,
      sceneCommandId,
      parentSceneCommandId,
    });
  };

  private handleRemoveEffectShowCommand = (
    sceneCommandForm: EffectShowSceneCommandForm,
  ) => {
    const {destroySceneCommandForm} = this.props;
    destroySceneCommandForm({sceneCommandForm});
  };

  private handleChangeEffectShowSceneCommandForm = (
    sceneCommandForm: EffectShowSceneCommandForm,
  ) => {
    const {updateSceneCommandForm} = this.props;
    updateSceneCommandForm({sceneCommandForm});
  };

  private handleForwardToChangeSoundEffects = (
    sceneCommandForm: SoundEffectShowSceneCommandForm,
    parentSceneCommandId?: number,
  ) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const sceneCommandId = sceneCommandForm.sceneCommandId;
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormSoundEffectShowSceneCommandEditNavigations(
      navigation,
      {
        storyId,
        episodeId,
        sceneCommandId,
        parentSceneCommandId,
      },
    );
  };

  private handleRemoveSoundEffectShowCommand = (
    sceneCommandForm: SoundEffectShowSceneCommandForm,
  ) => {
    const {destroySceneCommandForm} = this.props;
    destroySceneCommandForm({sceneCommandForm});
  };

  private handleForwardToChangeBackgroundMusic = (
    sceneCommandForm: BackgroundMusicShowSceneCommandForm,
    parentSceneCommandId?: number,
  ) => {
    const {navigation, route, sceneForm} = this.props;
    const {episodeId} = route.params;
    if (!sceneForm) {
      return;
    }
    const sceneCommandId = sceneCommandForm.sceneCommandId;
    const storyId = sceneForm.storyId;
    routers.linkToSceneFormBackgroundMusicShowSceneCommandEditNavigations(
      navigation,
      {
        storyId,
        episodeId,
        sceneCommandId,
        parentSceneCommandId,
      },
    );
  };

  private handleRemoveBackgroundMusicShowCommand = (
    sceneCommandForm: BackgroundMusicShowSceneCommandForm,
  ) => {
    const {destroySceneCommandForm} = this.props;
    destroySceneCommandForm({sceneCommandForm});
  };

  private handleInsertBackgroundMusicHideCommandAt = (
    sceneCommandForm: BackgroundMusicShowSceneCommandForm,
    sceneCommandIndex: number,
  ) => {
    const {createSceneCommandForm} = this.props;
    createSceneCommandForm({
      index: sceneCommandIndex,
      sceneCommandForm: new BackgroundMusicHideSceneCommandForm(
        sceneCommandForm.sound,
      ),
    });
  };

  private handleChangeOrder = (positionMap: Map<Position, Position>) => {
    const {
      sceneCommandForms: currentSceneCommandForms,
      updateSceneCommandFormLists,
      orientedSpeechBalloons,
      indexOrientedSpeechBalloons,
      orientedSpeechBalloonsParams,
    } = this.props;
    if (!currentSceneCommandForms) {
      return;
    }
    const callback = (records: OrientedSpeechBalloon[]) => {
      const sceneCommandForms = new SceneCommandFormsPositionSwitcher(
        records,
      ).switchPosition(currentSceneCommandForms, positionMap);
      updateSceneCommandFormLists({sceneCommandForms});
    };
    if (orientedSpeechBalloons) {
      callback(orientedSpeechBalloons);
    } else {
      indexOrientedSpeechBalloons(orientedSpeechBalloonsParams).then(result => {
        callback(result.records);
      });
    }
  };

  private handleChangeCommand = (
    sceneCommandForm: SceneCommandForm,
    characterPatternMapping?: CharacterPatternMapping,
  ) => {
    const {updateSceneCommandForm} = this.props;
    updateSceneCommandForm({sceneCommandForm, characterPatternMapping});
  };

  private handleRemoveCommand = (sceneCommandForm: SceneCommandForm) => {
    const {destroySceneCommandForm} = this.props;
    destroySceneCommandForm({sceneCommandForm});
  };

  private handleRequestOpenModal = (modalParams: ModalParams) => {
    this.setState({modalParams});
  };

  private handleRequestCloseModal = () => {
    this.setState({modalParams: null});
  };

  private handleRequestUpdateCoachmarkModal = (
    coachmarkState: CoachmarkState | null,
  ) => {
    const {updateCoachmarkModal} = this.props;
    updateCoachmarkModal({value: coachmarkState});
  };

  private handlePreview = () => {
    const {navigation, route, sceneForm, sceneCommandForms, episode} =
      this.props;
    const {episodeId} = route.params;
    if (!sceneForm || !sceneCommandForms || !episode) {
      return;
    }
    const params = this.createSceneParams(sceneForm, sceneCommandForms);
    routers.linkToPreviewSceneViewer(navigation, {
      params,
      episodeId,
    });
  };

  private handleSubmit = () => {
    const {
      navigation,
      route,
      sceneForm,
      sceneCommandForms,
      showEpisode,
      createScene,
    } = this.props;
    if (!sceneForm || !sceneCommandForms) {
      return;
    }
    const sceneParams = this.createSceneParams(sceneForm, sceneCommandForms);
    this.setState({disabledSubmitButton: true, loading: true}, () => {
      createScene(sceneParams)
        .then(scene => {
          this.removeListenerBeforeRemove();
          showEpisode(scene.episodeId, true);
          const {fromEpisode} = route.params;
          this.clearOldScene(() => {
            this.setState({loading: false}, () => {
              if (fromEpisode) {
                navigation.goBack();
              } else {
                const {episodeId} = scene;
                navigation.replace('Episode', {episodeId});
              }
            });
          });
        })
        .catch(error => {
          this.setState({
            disabledSubmitButton: false,
            loading: false,
            alertMessage: formatErrorMessages({}, error),
          });
        });
    });
  };

  private createSceneParams(
    sceneForm: SceneForm,
    sceneCommandForms: SceneCommandForm[],
  ): SceneScriptCreateParams {
    const episodeId = sceneForm.episodeId;
    const backgroundId = sceneForm.background ? sceneForm.background.id : null;
    const options = sceneForm.options;
    const positionedEffectId = sceneForm.positionedEffect?.id || null;
    const effectOptions = sceneForm.effectOptions;
    const commandsAttributes = new SceneCommandParamsBuilder().build(
      sceneCommandForms,
    );
    return {
      backgroundId,
      commandsAttributes,
      episodeId,
      options,
      positionedEffectId,
      effectOptions,
      scriptVersion: 2,
    };
  }

  private clearOldScene(callback?: () => void) {
    const {route, destroySceneForm} = this.props;
    const {episodeId} = route.params;
    this.editing = false;
    autoSaveCreatingScenesRepository.destroy(episodeId);
    destroySceneForm().then(() => {
      this.setState({sceneCommandForms: null}, () => {
        callback && callback();
      });
    });
  }

  private handleDelete = () => {
    const {sceneForm, sceneCommandForms} = this.props;
    const callback = this.state.beforeBackCallback;
    if (!callback) {
      return;
    }
    if (!sceneForm || !sceneCommandForms) {
      return;
    }
    this.purgeEpisode(sceneForm.episodeId, callback);
  };

  private purgeEpisode = async (episodeId: number, callback?: () => void) => {
    const {route, indexScenes, destroyEpisode} = this.props;
    const {episodePurgeForBack} = route.params;
    this.clearOldScene();
    const response = await indexScenes({episodeId});
    if (episodePurgeForBack && response.total === 0) {
      destroyEpisode(episodeId).then(callback);
    } else {
      callback && callback();
    }
  };

  private handleCancel = () => {
    this.setState({beforeBackCallback: null});
  };

  private handleRequestIndexActorCharacterFaces = (
    params: ActorCharacterFaceIndexParams,
  ) => {
    const {indexActorCharacterFaces} = this.props;
    return indexActorCharacterFaces(params);
  };

  private handleRequestIndexMarks = () => {
    const {indexMarks, marksParams} = this.props;
    return indexMarks(marksParams);
  };

  private handleRequestIndexSpeechBalloons = () => {
    const {indexSpeechBalloons, speechBalloonsParams, speechBalloons} =
      this.props;
    if (!speechBalloons) {
      indexSpeechBalloons(speechBalloonsParams);
    }
  };

  private handleRequestIndexOrientedSpeechBalloons = () => {
    const {
      indexOrientedSpeechBalloons,
      orientedSpeechBalloonsParams,
      orientedSpeechBalloons,
    } = this.props;
    if (!orientedSpeechBalloons) {
      indexOrientedSpeechBalloons(orientedSpeechBalloonsParams);
    }
  };

  private handleRequestIndexTextFrames = () => {
    const {indexTextFrames, textFramesParams, textFrames} = this.props;
    if (!textFrames) {
      indexTextFrames(textFramesParams);
    }
  };

  private handleRequestCreateCharacterPattern = async (
    params: CharacterPatternCreateParams,
  ): Promise<CharacterPattern> => {
    const {
      indexCharacterPatterns,
      createCharacterPattern,
      updateCharacterPatternUsageHistory,
    } = this.props;
    let characterPattern = (await indexCharacterPatterns(params)).records[0];
    if (!characterPattern) {
      characterPattern = await createCharacterPattern(params);
    }
    updateCharacterPatternUsageHistory(characterPattern.id);
    return characterPattern;
  };

  private handleRequestIndexActorCostumes = (
    params: ActorCostumeIndexParams,
  ) => {
    const {indexActorCostumes} = this.props;
    return indexActorCostumes(params);
  };

  private handleRequestIndexActorHairStyles = (
    params: ActorHairStyleIndexParams,
  ) => {
    const {indexActorHairStyles} = this.props;
    return indexActorHairStyles(params);
  };

  private handleRequestIndexActorAccessorySets = (
    params: ActorAccessorySetIndexParams,
  ) => {
    const {indexActorAccessorySets} = this.props;
    return indexActorAccessorySets(params);
  };

  private handleRequestUpdateModal = (modalParams: ModalParams) => {
    this.setState({modalParams});
  };

  private handleCloseModal = () => {
    this.setState({alertMessage: undefined});
  };

  private handleCloseRestoredModal = () => {
    this.setState({visibleRestoredModal: false});
  };

  private setStateIfMounted<K extends keyof State>(
    state:
      | ((
          prevState: Readonly<State>,
          props: Readonly<Props>,
        ) => Pick<State, K> | State | null)
      | (Pick<State, K> | State | null),
    callback?: () => void,
  ): void {
    if (!this.mounted) {
      return;
    }
    this.setState(state as any, callback);
  }
}
